import type { ArticleMeta } from '@/features/blog/types'
import {
  SITE_NAME,
  coverImageUrl,
  getSiteOrigin,
  jsonLdScript,
  markdownAlternate,
  truncateDescription,
  type HeadResult,
  type MetaTag,
} from './siteMeta'

function articleUrl(slug: string, origin: string): string {
  return `${origin}/inspirace/${slug}`
}

/** Resolve a cover image to an absolute URL (passes through absolute URLs). */
function resolveImageUrl(coverImage: string, origin: string): string {
  if (!coverImage) return coverImageUrl(origin)
  if (/^https?:\/\//.test(coverImage)) return coverImage
  return `${origin}${coverImage}`
}

function buildBlogPostingJsonLd(
  meta: ArticleMeta,
  url: string,
  image: string,
  origin: string,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: meta.title,
    description: meta.perex,
    image: [image],
    url,
    datePublished: meta.publishedAt,
    dateModified: meta.publishedAt,
    inLanguage: 'cs-CZ',
    author: { '@type': 'Person', name: meta.author },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${origin}/img/logo_matevolno.svg`,
      },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  }
}

function buildBreadcrumbJsonLd(meta: ArticleMeta, url: string, origin: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Domů', item: origin },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Inspirace',
        item: `${origin}/inspirace`,
      },
      { '@type': 'ListItem', position: 3, name: meta.title, item: url },
    ],
  }
}

/**
 * Build per-article head tags (title, description, Open Graph, Twitter,
 * canonical link, `BlogPosting` and `BreadcrumbList` JSON-LD). Shared by the
 * runtime article route and the build-time prerender script. Pass `origin`
 * explicitly when running outside the browser (e.g. the build script).
 */
export function buildArticleHead(meta: ArticleMeta, origin: string = getSiteOrigin()): HeadResult {
  const url = articleUrl(meta.slug, origin)
  const image = resolveImageUrl(meta.coverImage, origin)
  const title = `${meta.metaTitle ?? meta.title} | ${SITE_NAME}`
  const description = truncateDescription(meta.perex)

  const tags: MetaTag[] = [
    { title },
    { name: 'description', content: description },
    { property: 'og:type', content: 'article' },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:locale', content: 'cs_CZ' },
    { property: 'og:url', content: url },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: image },
    { property: 'og:image:alt', content: meta.coverImageAlt },
    { property: 'article:published_time', content: meta.publishedAt },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:url', content: url },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
  ]

  return {
    meta: tags,
    links: [{ rel: 'canonical', href: url }, markdownAlternate(`${url}.md`)],
    scripts: [
      jsonLdScript(buildBlogPostingJsonLd(meta, url, image, origin)),
      jsonLdScript(buildBreadcrumbJsonLd(meta, url, origin)),
    ],
  }
}
